import { useCallback, useState } from "react"
import { AlertColor } from "@mui/material"
import { saveProducts } from "@/shared/services/products"
import { GallerySection } from "@/shared/types/product"

type SaveProductsResult = Awaited<ReturnType<typeof saveProducts>>

type GenerationStatus = "success" | "blocked" | "error"

type GenerationOutcome = { status: "success"; result: SaveProductsResult } | { status: "blocked" | "error" }

interface UseGenerateWithAIActionOptions {
  userId: string | null
  notify: (message: string, type?: AlertColor) => void
  sectionsFactory: () => GallerySection[]
  canGenerateImage?: boolean
  onRequireUpgrade?: () => void
  onSuccess?: (result: SaveProductsResult) => void | Promise<void>
  successMessage: string
  failureMessage: string
}

interface UseGenerateWithAIActionResult {
  loading: boolean
  run: () => Promise<GenerationOutcome>
}

const isQuotaError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false
  }

  const code = (error as { code?: unknown }).code
  return code === "functions/resource-exhausted"
}

const useGenerateWithAIAction = ({
  userId,
  notify,
  sectionsFactory,
  canGenerateImage = true,
  onRequireUpgrade,
  onSuccess,
  successMessage,
  failureMessage
}: UseGenerateWithAIActionOptions): UseGenerateWithAIActionResult => {
  const [loading, setLoading] = useState(false)

  const run = useCallback(async (): Promise<GenerationOutcome> => {
    if (!userId) {
      notify("Please sign in to generate images.", "error")
      return { status: "blocked" }
    }

    if (!canGenerateImage) {
      onRequireUpgrade?.()
      return { status: "blocked" }
    }

    setLoading(true)
    try {
      const result = await saveProducts(userId, sectionsFactory())
      if (result.skipped > 0) {
        notify(result.message, "warning")
      } else {
        notify(successMessage, "success")
      }

      await onSuccess?.(result)
      return { status: "success", result }
    } catch (error: unknown) {
      console.error("Error uploading images and saving product:", error)
      if (isQuotaError(error)) {
        onRequireUpgrade?.()
        return { status: "blocked" }
      }

      notify(failureMessage, "error")
      return { status: "error" }
    } finally {
      setLoading(false)
    }
  }, [canGenerateImage, failureMessage, notify, onRequireUpgrade, onSuccess, sectionsFactory, successMessage, userId])

  return { loading, run }
}

export { useGenerateWithAIAction }
export type { GenerationOutcome, GenerationStatus, SaveProductsResult, UseGenerateWithAIActionOptions }
